import React, { useEffect } from 'react'
import chroma from 'chroma-js';
import { Box } from 'rebass';
import { Theme, Color, BackgroundColor } from '@adobe/leonardo-contrast-colors';

import useLocalStorage from './hooks/useLocalStorage';
import { isCompositeComponent } from 'react-dom/test-utils';

// Palette configuration
export const semanticAliases = {
  Gray: 'Neutral',
  Green: 'Success',
  Blue: 'Primary',
  Red: 'Danger',
  Yellow: 'Warning',
}
const paletteMap = [
  {
    name: "Gray",
    colorKeys: ["#F7F9FA", "#F0F3F5", "#87949b", "#29363d"],
    // colorKeys: ["#F7F9FA", "#F0F3F5", "#9c9a87", "#3d3c29"],
    colorspace: "CAM02",
    background: true,
  },
  {
    name: "Blue",
    colorKeys: ["#00435f", "#007da0", "#00b6e9"],
    colorspace: "LAB",
  },
  {
    name: "Red",
    colorKeys: ["#61201c", "#f55f55", "#e42121", "#ff5151"],
    colorspace: "HSL",
  },
  {
    name: "Green",
    colorKeys: ["#008E41", "#5AD091"],
    colorspace: "LAB",
  },
  {
    name: "Yellow",
    colorKeys: ["#fed16c", "#9e5c00"],
    colorspace: "LAB",
  },
  {
    name: "Orange",
    colorKeys: ["#ff9147", "#bd4200"],
    colorspace: "LAB",
  },
  {
    name: "Purple",
    colorKeys: ["#7829b6"],
    colorspace: "LAB",
  },
];
export const ratios = color => ({
  [`${color}50`]: 1.05,
  [`${color}100`]: 1.25,
  [`${color}200`]: 1.5,
  [`${color}300`]: 2.3,
  [`${color}400`]: 4.6,
  [`${color}500`]: 5.7,
  [`${color}600`]: 7.18,
  [`${color}700`]: 9.44,
  [`${color}800`]: 12.3,
  [`${color}900`]: 17.19
});


// Utils
export const palettes = ({ alias }) => paletteMap.map(p => {
  if (alias && semanticAliases[p.name]) {
    return semanticAliases[p.name]
  }
  return p.name;
});
export const palettesWithoutGray = ({ alias }) => palettes({ alias }).filter(palette => palette !== 'Gray');
export const replaceAlias = (str, useSemanticAlias) => {
  if (!useSemanticAlias) {
    return str;
  }
  
  // if str is a scale color just return str
  const regexr = /([A-z]*)\d{2,3}/;
  const match = regexr.exec(str);
  if (match) {
    return str;
  }

  const aliasKey = Object.keys(semanticAliases).find(k => str.indexOf(k) > -1);
  const aliasKeyLowerCase = Object.keys(semanticAliases).find(k => str.toLowerCase().indexOf(k.toLowerCase()) > -1);
  if (aliasKey) {
    return str.replace(aliasKey, semanticAliases[aliasKey]);
  } else if (aliasKeyLowerCase) {
    return str.replace(aliasKeyLowerCase.toLowerCase(), semanticAliases[aliasKeyLowerCase].toLowerCase());
  }

  return str;
}
export function getValue(key, colors, format) {
  const value = colors[key];

  // If alias
  if (value.indexOf('$') === 0) {
    if (format) {
      if (format === 'figma') {
        const regexr = /([A-z]*)\d{2,3}/;
        const isScaleColor = regexr.exec(value);
        if (isScaleColor) {
          return `{Scales.${value.replace('$', '')}}`;
        } 
        return `{${value.replace('$', '')}}`;
      } else if (format === 'hex') {
        return getValue(value.replace('$', ''), colors, format);
      }
    }
    return `var(--${value.replace('$', '')})`;
  
    // If modified with alpha
  } else if (value.indexOf('alpha') === 0){
    const [_, colorKey, amount] = value.split(' ');
    const alphaColor = chroma(getValue(colorKey.replace('$', ''), colors)).alpha(parseFloat(amount)).css();
    return alphaColor;

  // If modified with lighten
  } else if (value.indexOf('lighten') === 0){
    const [_, colorKey, amount] = value.split(' ');
    return chroma(getValue(colorKey.replace('$', ''), colors)).brighten(parseFloat(amount)).css();

  // If modified with darken
  } else if (value.indexOf('darken') === 0) {
    const [_, colorKey, amount] = value.split(' ');
    return chroma(getValue(colorKey.replace('$', ''), colors)).darken(parseFloat(amount)).css();

  } else {
    return value;
  }
};
const makeCssVariableDeclaration = (key, theme) => {
  return `--${key}: ${getValue(key, theme)};`
}
export function getScalePaletteColors(includeGray = false) {
  let func = includeGray ? palettes : palettesWithoutGray;

  return func({ alias: false }).map(palette => {
    return ({
      scale: palette.toLowerCase(),
      scaleUppercase: palette,
      palette: replaceAlias(palette.toLowerCase(), true),
      paletteUppercase: replaceAlias(palette, true),
    });
  });
}


// Theme setup
const createThemeColors = (lightness, contrast, alias) => {
  const paletteObjects = paletteMap.map(p => p.background ? new BackgroundColor({
    name: replaceAlias(p.name, alias),
    colorKeys: p.colorKeys,
    ratios: ratios('gray'),
    colorspace: p.colorspace,
  }) : new Color({
    name: replaceAlias(p.name, alias),
    colorKeys: p.colorKeys,
    ratios: ratios(p.name),
    colorspace: p.colorspace,
  }));

  let MyTheme = new Theme({
    colors: paletteObjects,
    backgroundColor: paletteObjects[0],
    lightness,
    contrast
  });

  const colors = {
    background: MyTheme._contrastColors[0].background,
  };
  MyTheme._contrastColors.forEach((color) => {
    if (!color.name) return;
    color.values.forEach(value => {
      colors[value.name.toLowerCase()] = value.value;
    });
  });

  return {
    colors,
    scales: MyTheme._contrastColors.filter(scale => scale.name).map(scale => scale.name.toLowerCase())
  }
}

const ThemeSwitcher = ({ handleSetTheme }) => {
  const [theme, setTheme] = useLocalStorage('theme', 'dark');
  const useSemanticAlias = true;

  const contrast = 1;
  const lightColorScale = createThemeColors(100, contrast, useSemanticAlias);
  const darkColorScale = createThemeColors(3, contrast, useSemanticAlias);

  const scalePalettes = getScalePaletteColors();

  const makeScalePalettes = (callback) => {
    return scalePalettes.reduce((current, props) => ({
      ...current,
      ...callback(props),
    }), {});
  };

  const themes = {
    light: {
      white: '#ffffff',
      black: '#000000',

      ...lightColorScale.colors,
      
      // Text
      textNeutral: '$gray800',
      textNeutralMuted: '$gray700',
      textNeutralSubtle: '$gray500',
      textOnBackgroundNeutral: 'white',

      ...makeScalePalettes(({ scale, paletteUppercase }) => ({
        [`text${paletteUppercase}`]: `$${scale}500`,
        [`text${paletteUppercase}Hover`]: `darken $${scale}500 0.75`,
        [`text${paletteUppercase}Active`]: `darken $${scale}500 1.5`,
        [`textOnBackground${paletteUppercase}`]: `white`,
      })),

      'textOnBackgroundWarning': `$yellow900`,
      'textOnBackgroundOrange': `$orange900`,

      // Icon
      iconNeutral: '$gray400',
      iconInactive: '$gray200',

      ...makeScalePalettes(({ scale, paletteUppercase }) => ({
        [`icon${paletteUppercase}`]: `$${scale}400`,
      })),

      // Background
      backgroundHover: '$gray50',
      backgroundHoverAlpha: 'alpha $gray800 0.05',
      backgroundActive: '$gray100',
      backgroundActiveAlpha: 'alpha $gray800 0.09',
      backgroundSurface: 'white',
      
      backgroundNeutralEmphasis: `$gray800`,
      backgroundNeutralEmphasisHover: `darken $gray500 0.25`,
      backgroundNeutralEmphasisActive: `darken $gray500 0.75`,

      backgroundNeutral: `$gray500`,
      backgroundNeutralHover: `darken $gray500 0.25`,
      backgroundNeutralActive: `darken $gray500 0.75`,

      backgroundNeutralMuted: '$gray100',
      backgroundNeutralMutedHover: `darken $gray100 0.25`,
      backgroundNeutralMutedActive: `darken $gray100 0.75`,

      backgroundNeutralMutedAlpha: `alpha $gray200 0.2`,
      backgroundNeutralMutedAlphaHover: `alpha $gray200 0.4`,
      backgroundNeutralMutedAlphaActive: `alpha $gray200 0.6`,

      backgroundNeutralSubtle: '$gray50',
      backgroundNeutralSubtleHover: `darken $gray50 0.25`,
      backgroundNeutralSubtleActive: `darken $gray50 0.75`,

      ...makeScalePalettes(({ scale, paletteUppercase }) => ({
        [`background${paletteUppercase}`]: `$${scale}400`,
        [`background${paletteUppercase}Hover`]: `darken $${scale}400 0.25`,
        [`background${paletteUppercase}Active`]: `darken $${scale}400 0.75`,

        [`background${paletteUppercase}Muted`]: `$${scale}100`,
        [`background${paletteUppercase}MutedHover`]: `darken $${scale}100 0.1`,
        [`background${paletteUppercase}MutedActive`]: `darken $${scale}100 0.2`,

        [`background${paletteUppercase}MutedAlpha`]: `alpha $${scale}200 0.4`,
        [`background${paletteUppercase}MutedHoverAlpha`]: `alpha $${scale}300 0.5`,
        [`background${paletteUppercase}MutedActiveAlpha`]: `alpha $${scale}400 0.6`,

        [`background${paletteUppercase}Subtle`]: `$${scale}50`,
        [`background${paletteUppercase}SubtleHover`]: `darken $${scale}50 0.25`,
        [`background${paletteUppercase}SubtleActive`]: `darken $${scale}50 0.75`,

        [`background${paletteUppercase}SubtleAlpha`]: `alpha $${scale}200 0.2`,
        [`background${paletteUppercase}SubtleHoverAlpha`]: `alpha $${scale}300 0.3`,
        [`background${paletteUppercase}SubtleActiveAlpha`]: `alpha $${scale}400 0.4`,
      })),

      'backgroundWarning': `$yellow200`,
      'backgroundWarningHover': `darken $yellow200 0.25`,
      'backgroundWarningActive': `darken $yellow200 0.5`,
      'backgroundOrange': `$orange300`,
      'backgroundOrangeHover': `darken $orange300 0.25`,
      'backgroundOrangeActive': `darken $orange300 0.5`,

      // Borders
      borderNeutral: `$gray100`,
      borderNeutralAlpha: 'alpha $gray800 0.2',
      borderNeutralMuted: `lighten $gray100 0.3`,
      borderNeutralMutedAlpha: 'alpha $gray800 0.1',
      borderNeutralSubtle: `lighten $gray100 0.8`,
      borderNeutralSubtleAlpha: 'alpha $gray800 0.05',

      ...makeScalePalettes(({ scale, paletteUppercase }) => ({
        [`border${paletteUppercase}`]: `$${scale}400`,
        [`border${paletteUppercase}Muted`]: `alpha $${scale}400 0.4`,
        [`border${paletteUppercase}Subtle`]: `alpha $${scale}400 0.2`,
      })),

      borderWarning: `$yellow200`,
      borderWarningMuted: `alpha $yellow200 0.4`,
      borderOrange: `$orange300`,
      borderOrangeMuted: `alpha $orange300 0.4`,

      // Utils
      focus: 'rgba(0, 133, 239, 0.6)',
    },
    dark: {
      ...darkColorScale.colors,

      white: '#fff',
      black: '#000',
      
      // Text
      'textNeutral': '$gray800',
      'textNeutralMuted': '$gray700',
      'textNeutralSubtle': '$gray500',
      'textOnBackgroundNeutral': 'white',

      ...makeScalePalettes(({ scale, paletteUppercase }) => ({
        [`text${paletteUppercase}`]: `$${scale}500`,
        [`text${paletteUppercase}Hover`]: `darken $${scale}500 0.75`,
        [`text${paletteUppercase}Active`]: `darken $${scale}500 1.5`,
        [`textOnBackground${paletteUppercase}`]: `white`,
      })),

      // Icon
      'iconNeutral': '$gray400',
      'iconInactive': '$gray200',

      ...makeScalePalettes(({ scale, paletteUppercase }) => ({
        [`icon${paletteUppercase}`]: `$${scale}400`,
      })),

      // Background
      "backgroundHover": '$gray50',
      "backgroundHoverAlpha": 'alpha $gray800 0.05',
      "backgroundActive": '$gray100',
      "backgroundActiveAlpha": 'alpha $gray800 0.09',

      'backgroundSurface': '$gray100',

      'backgroundNeutralEmphasis': `$gray800`,
      'backgroundNeutral': `$gray500`,
      'backgroundNeutralHover': `darken $gray500 0.25`,
      'backgroundNeutralActive': `darken $gray500 0.75`,
      'backgroundNeutralMuted': '$gray100',
      'backgroundNeutralMutedHover': `darken $gray100 0.25`,
      'backgroundNeutralMutedActive': `darken $gray100 0.75`,
      [`backgroundNeutralMutedAlpha`]: `alpha $gray200 0.2`,
      'backgroundNeutralSubtle': '$gray50',

      ...makeScalePalettes(({ scale, paletteUppercase }) => ({
        [`background${paletteUppercase}`]: `$${scale}400`,
        [`background${paletteUppercase}Hover`]: `darken $${scale}400 0.25`,
        [`background${paletteUppercase}Active`]: `darken $${scale}400 0.75`,

        [`background${paletteUppercase}Muted`]: `$${scale}100`,
        [`background${paletteUppercase}MutedHover`]: `darken $${scale}100 0.1`,
        [`background${paletteUppercase}MutedActive`]: `darken $${scale}100 0.2`,

        [`background${paletteUppercase}MutedAlpha`]: `alpha $${scale}200 0.4`,
        [`background${paletteUppercase}MutedHoverAlpha`]: `alpha $${scale}300 0.5`,
        [`background${paletteUppercase}MutedActiveAlpha`]: `alpha $${scale}400 0.6`,

        [`background${paletteUppercase}Subtle`]: `$${scale}50`,
        [`background${paletteUppercase}SubtleAlpha`]: `alpha $${scale}200 0.2`,
      })),

      // Borders
      borderNeutral: `$gray100`,
      borderNeutralAlpha: 'alpha $gray800 0.2',
      borderNeutralMuted: `lighten $gray100 0.3`,
      borderNeutralMutedAlpha: 'alpha $gray800 0.1',
      borderNeutralSubtle: `lighten $gray100 0.8`,
      borderNeutralSubtleAlpha: 'alpha $gray800 0.05',
      ...makeScalePalettes(({ scale, paletteUppercase }) => ({
        [`border${paletteUppercase}`]: `$${scale}400`,
        [`border${paletteUppercase}Muted`]: `alpha $${scale}400 0.4`,
        [`border${paletteUppercase}Subtle`]: `alpha $${scale}400 0.2`,
      })), 

      // Utils
      focus: '#0085ef99',
    },
  };

  useEffect(() => {
    let defaultTheme = 'light';
    document.body.dataset.theme = defaultTheme;

    // let _theme = theme;
    // if (!_theme) {
    //   if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    //     _theme = 'dark';
    //   } else {
    //     _theme = 'light';
    //   }
    // }

    handleSetTheme({
      theme: defaultTheme,
      themes,
    });
  }, [theme]);

  useEffect(() =>  {
    // create themed style tag with color variables
    const style = document.createElement('style');
    style.type = 'text/css';
    style.innerHTML = Object.keys(themes).reduce((prev, themeKey) => {
      let result = prev;
      // ${Object.keys(themes[themeKey]).map(key => `--${key}: ${getValue(key, themes[themeKey])};`).join('\n')}
      result += `
        body[data-theme="${themeKey}"] {
          ${Object.keys(themes[themeKey]).map(key => makeCssVariableDeclaration(key, themes[themeKey])).join('\n')}
        }
      `;
      return result;
    }, '');
    style.innerHTML += `
      @media (prefers-color-scheme: dark) {
        body {
          ${Object.keys(themes.dark).map(key => makeCssVariableDeclaration(key, themes.dark)).join('\n')}
        }
      }
      @media (prefers-color-scheme: light) {
        body {
          ${Object.keys(themes.light).map(key => makeCssVariableDeclaration(key, themes.light)).join('\n')}
        }
      }
    `;
    document.head.appendChild(style);

    // window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', event => {
    //   const newColorScheme = event.matches ? 'dark' : 'light';
    //   handleSetTheme({
    //     theme: newColorScheme,
    //     themes,
    //   });
    // });

    // setTheme(document.body.dataset.theme);
  }, []);

  return null;

  return (
    <Box
      as="select"
      id="theme-selector"
      onChange={e => {
        setTheme(e.target.value)
        handleSetTheme({
          theme: e.target.value,
          themes,
        });
      }}
      value={theme}
      mr="10px"
    >
      {Object.keys(themes).map(theme => <option key={theme} value={theme}>{theme}</option>)}
      <option value="">Use system preference</option>
    </Box>
  )
}

export default ThemeSwitcher